import { AvailableLanguages } from '@repo/toolkit/language-types'
import { toFullName } from '@repo/toolkit/to-full-name'
import { TFunction } from 'i18next'
import { electronicFormat, isValid } from 'iban-ts'
import { z, ZodType } from 'zod'

import { LeaderFreeRateEnum } from '../../../lib/types/enums/activity/leader-free-rate.enum'
import { TransportModeEnum } from '../../../lib/types/enums/activity/transport-mode.enum'
import { CurrencyEnum } from '../../../lib/types/enums/common/currency.enum'
import { GenderEnum } from '../../../lib/types/enums/common/gender.enum'
import { OutingDurationEnum } from '../../../lib/types/enums/cultural-outing/outing-duration.enum'
import { OLTypeEnum } from '../../../lib/types/enums/ol/ol-type.enum'
import { ArrivalAndDepartureInput } from '../../../lib/types/models/arrival-and-departure/arrival-and-departure-input'
import { CulturalOutingInput } from '../../../lib/types/models/cultural-outing/cultural-outing-input'
import { ActivityInput } from '../../../lib/types/models/ol/activity/activity-input'
import { CoachCompanyInput } from '../../../lib/types/models/ol/coach-company/coach-company-input'
import { OlCommission } from '../../../lib/types/models/ol/commission/ol-commission'
import { OlCommissionInput } from '../../../lib/types/models/ol/commission/ol-commission-input'
import { OLHostFamilyPricePerNight } from '../../../lib/types/models/ol/host-family-price-per-night/ol-host-family-price-per-night'
import { OLHostFamilyPricePerNightInput } from '../../../lib/types/models/ol/host-family-price-per-night/ol-host-family-price-per-night-input'
import { Ol } from '../../../lib/types/models/ol/ol'
import {
  OlBankAccountInput,
  OlCommentInput,
  OlContactInput,
  OlInput,
  OlJourneyPlaceInput
} from '../../../lib/types/models/ol/ol-input'
import { OlTeflPrice } from '../../../lib/types/models/ol/tefl-price/ol-tefl-price'
import { OlTeflPriceInput } from '../../../lib/types/models/ol/tefl-price/ol-tefl-price-input'
import { QuotationInput } from '../../../lib/types/models/quotation/quotation-input'
import { createDateSchema } from '../../../lib/utils/validations/date.validation'
import { createEnumSchema } from '../../../lib/utils/validations/enum-validation'
import { FILE_VALIDATION } from '../../../lib/utils/validations/file.validation'
import {
  createNumberArraySchema,
  createNumberSchema
} from '../../../lib/utils/validations/int-array-validation'
import { PHONE_NUMBER_SCHEMA } from '../../../lib/utils/validations/phone-number.validation'
import { createStringSchema } from '../../../lib/utils/validations/string-validation'
import { t } from '../../../locale/utils'

export const OL_BANK_ACCOUNT_FORM_SCHEMA_VALIDATION = (
  language: AvailableLanguages
): ZodType<OlBankAccountInput> =>
  z
    .object({
      bankAccountHolder: createStringSchema('Titulaire du compte bancaire'),
      // USD accounts (e.g. US bank accounts) have no IBAN, so the IBAN format
      // check only applies to the other currencies (validated below)
      iban: createStringSchema('IBAN', { maximumLength: 34 }),
      bic: createStringSchema('BIC'),
      currency: createEnumSchema({ fieldName: 'Devise', enumType: CurrencyEnum }),
      hostCountries: z.array(
        z.object({
          id: createNumberSchema('Pays de séjour', { isInteger: true }),
          name: createStringSchema('Pays de séjour')
        }),
        { required_error: 'Au moins un pays de séjour doit être renseigné' }
      )
    })
    .superRefine((data, ctx) => {
      if (data.currency === CurrencyEnum.USD) {
        // US accounts have no IBAN: the pain.001 export writes the value as an
        // <Othr><Id> identifier, which must be alphanumeric
        const accountNumber = data.iban.replace(/\s+/g, '')
        if (!/^[a-zA-Z0-9]+$/.test(accountNumber)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['iban'],
            message: t('errors.iban.invalidUsdAccountNumber', { lng: language })
          })
        }
      } else if (!isValid(data.iban)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['iban'],
          message: t('errors.iban.invalid', { lng: language })
        })
      }
    })
    .transform((data) => ({
      ...data,
      iban:
        data.currency === CurrencyEnum.USD
          ? data.iban.replace(/\s+/g, '').toUpperCase()
          : electronicFormat(data.iban)
    }))

export const OL_CONTACT_FORM_SCHEMA_VALIDATION: (
  t: TFunction<AvailableLanguages, undefined>
) => ZodType<OlContactInput> = (t) =>
  z.object({
    firstName: createStringSchema('Prénom'),
    lastName: createStringSchema('NOM'),
    email: createStringSchema('Email', { isEmail: true }),
    phone: PHONE_NUMBER_SCHEMA(t),
    position: createStringSchema('Poste')
  })

export const OL_COMMENT_FORM_SCHEMA_VALIDATION: ZodType<OlCommentInput> = z.object({
  comment: createStringSchema('Commentaire'),
  commentedByName: createStringSchema('Commenté par'),
  date: createDateSchema({ inputName: 'Date' })
})

export const OL_JOURNEY_PLACE_FORM_SCHEMA_VALIDATION: ZodType<OlJourneyPlaceInput> = z.object({
  id: createNumberSchema('ID', { nullish: true }),
  name: createStringSchema('NOM'),
  address1: createStringSchema('Adresse'),
  address2: createStringSchema("Complément d'adresse", { nullish: true }),
  city: createStringSchema('Ville'),
  zipCode: createStringSchema('Code postal'),
  country: createStringSchema('Pays'),
  isSameAddresForTuition: z.boolean(),
  tuitionName: createStringSchema('NOM du centre de formation', { nullish: true }),
  tuitionAddress1: createStringSchema('Adresse du centre de formation', { nullish: true }),
  tuitionAddress2: createStringSchema("Complément d'adresse du centre de formation", {
    nullish: true
  }),
  tuitionCity: createStringSchema('Ville du centre de formation', { nullish: true }),
  tuitionZipCode: createStringSchema('Code postal du centre de formation', { nullish: true }),
  tuitionCountry: createStringSchema('Pays du centre de formation', { nullish: true })
})

export const OL_FORM_SCHEMA_VALIDATION = (
  t: TFunction<AvailableLanguages, undefined>,
  language: AvailableLanguages
) =>
  z
    .object({
      type: createEnumSchema({
        fieldName: 'Type',
        enumType: OLTypeEnum,
        nullish: true,
        language
      }),
      isActive: z.boolean(),
      isOtoIndividual: z.boolean().default(false),
      contactInParisId: createNumberSchema('Contact à Paris', {
        isInteger: true,
        nullish: true,
        language
      }),
      mainContactPicture: FILE_VALIDATION({ language }).nullish(),
      mainContactGender: createEnumSchema({
        fieldName: 'Genre',
        enumType: GenderEnum,
        nullish: true,
        language
      }),
      currency: createEnumSchema({
        fieldName: 'Devise',
        enumType: CurrencyEnum,
        nullish: true,
        language
      }),
      mainContactBirthDate: createStringSchema('Date de naissance', { nullish: true, language }),
      mainContactFirstName: createStringSchema('Prénom', { nullish: true, language }),
      mainContactLastName: createStringSchema('NOM', { nullish: true, language }),
      mainContactPosition: createStringSchema('Poste', { nullish: true, language }),
      mainContactEmail: createStringSchema('Email', { nullish: true, isEmail: true, language }),
      mainContactPhone: PHONE_NUMBER_SCHEMA(t).nullish(),
      mainContactPhone2: PHONE_NUMBER_SCHEMA(t).nullish(),
      address1: createStringSchema('Adresse', { nullish: true, language }),
      address2: createStringSchema("Complément d'adresse", { nullish: true, language }),
      city: createStringSchema('Ville', { nullish: true, language }),
      zipCode: createStringSchema('Code postal', { nullish: true, language }),
      country: createStringSchema('Pays', { nullish: true, language }),
      hostCountries: createNumberArraySchema('Pays de séjour').default([]),
      hostPlaces: createNumberArraySchema('Lieux de séjour').default([]),
      files: FILE_VALIDATION().array().default([]),
      bankAccounts: z.array(OL_BANK_ACCOUNT_FORM_SCHEMA_VALIDATION(language)).default([]),
      olContacts: z.array(OL_CONTACT_FORM_SCHEMA_VALIDATION(t)).default([]),
      comments: z.array(OL_COMMENT_FORM_SCHEMA_VALIDATION).default([]),
      journeyPlaces: z.array(OL_JOURNEY_PLACE_FORM_SCHEMA_VALIDATION).default([]),
      relatedToOlIds: createNumberArraySchema('Prestataires liés').nullish()
    })
    .superRefine((data, ctx) => {
      // Type is always required
      if (!data.type) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Type est obligatoire',
          path: ['type']
        })
      }

      // Currency is always required
      if (!data.currency) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Devise est obligatoire',
          path: ['currency']
        })
      }

      if (!data.mainContactLastName || data.mainContactLastName.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'NOM est obligatoire',
          path: ['mainContactLastName']
        })
      }

      // Email is always required and must be valid
      if (!data.mainContactEmail || data.mainContactEmail.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Email est obligatoire',
          path: ['mainContactEmail']
        })
      }

      // Phone is always required
      if (!data.mainContactPhone || data.mainContactPhone.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Téléphone est obligatoire',
          path: ['mainContactPhone']
        })
      }

      // Address fields are always required
      if (!data.address1 || data.address1.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Adresse est obligatoire',
          path: ['address1']
        })
      }

      if (!data.city || data.city.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Ville est obligatoire',
          path: ['city']
        })
      }

      if (!data.zipCode || data.zipCode.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Code postal est obligatoire',
          path: ['zipCode']
        })
      }

      if (!data.country || data.country.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Pays est obligatoire',
          path: ['country']
        })
      }

      // Host countries and places are required
      if (!data.hostCountries || data.hostCountries.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Au moins un pays de séjour est obligatoire',
          path: ['hostCountries']
        })
      }

      if (!data.hostPlaces || data.hostPlaces.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Au moins un lieu de séjour est obligatoire',
          path: ['hostPlaces']
        })
      }

      // If gender is not null (i.e., Male or Female), these fields are required
      // If gender is null (Société) or the provider is Divers, these fields are optional/nullable
      if (
        data.mainContactGender !== null &&
        data.mainContactGender !== undefined &&
        data.type !== OLTypeEnum.DIVERS
      ) {
        // Position is always required
        if (!data.mainContactPosition || data.mainContactPosition.trim() === '') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Poste est obligatoire',
            path: ['mainContactPosition']
          })
        }
        if (!data.mainContactFirstName || data.mainContactFirstName.trim() === '') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Prénom est obligatoire',
            path: ['mainContactFirstName']
          })
        }
        if (!data.mainContactBirthDate || data.mainContactBirthDate.trim() === '') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Date de naissance est obligatoire',
            path: ['mainContactBirthDate']
          })
        }
      }
    })

export const mapOlToInput = (ol: Ol): OlInput => ({
  address1: ol.address1,
  city: ol.city,
  country: ol.country,
  files: ol.files?.map((file) => ({
    name: file.name,
    path: file.path,
    size: file.size,
    type: file.type
  })),
  currency: ol.currency,
  hostCountries: ol.hostCountries.map((hc) => hc.id),
  hostPlaces: ol.hostPlaces.map((hp) => hp.id),
  isActive: ol.isActive,
  isOtoIndividual: ol.isOtoIndividual,
  mainContactBirthDate: ol.mainContactBirthDate,
  mainContactEmail: ol.mainContactEmail,
  mainContactFirstName: ol.mainContactFirstName,
  mainContactLastName: ol.mainContactLastName,
  mainContactGender: ol.mainContactGender,
  mainContactPhone: ol.mainContactPhone,
  mainContactPosition: ol.mainContactPosition,
  journeyPlaces: ol.journeyPlaces.map((jp) => ({
    id: jp.id,
    address1: jp.address1,
    address2: jp.address2,
    city: jp.city,
    country: jp.country,
    name: jp.name,
    zipCode: jp.zipCode,
    isSameAddresForTuition: jp.isSameAddresForTuition,
    tuitionAddress1: jp.tuitionAddress1,
    tuitionAddress2: jp.tuitionAddress2,
    tuitionCity: jp.tuitionCity,
    tuitionCountry: jp.tuitionCountry,
    tuitionName: jp.tuitionName,
    tuitionZipCode: jp.tuitionZipCode
  })),
  type: ol.type,
  zipCode: ol.zipCode,
  address2: ol.address2,
  bankAccounts: ol.bankAccounts.map((ba) => ({
    id: ba.id,
    bankAccountHolder: ba.bankAccountHolder,
    bic: ba.bic,
    currency: ba.currency,
    hostCountries: ba.hostCountries.map((hc) => ({ id: hc.id, name: hc.name })),
    iban: ba.iban
  })),
  comments: ol.comments.map((c) => ({
    id: c.id,
    comment: c.comment,
    commentedByName: toFullName(c.commentedBy.firstName, c.commentedBy.lastName),
    date: c.date
  })),
  mainContactPhone2: ol.mainContactPhone2,
  mainContactPicture: ol.mainContactPicture,
  contactInParisId: ol.contactInParis?.id,
  olContacts: ol.olContacts.map((oc) => ({
    id: oc.id,
    firstName: oc.firstName,
    lastName: oc.lastName,
    email: oc.email,
    phone: oc.phone,
    position: oc.position
  })),
  relatedToOlIds: ol.relatedToOls?.map((relatedOl) => relatedOl.id)
})

export const mapOlCommissionToInput = (olCommission: OlCommission): OlCommissionInput => ({
  durationsInDays: olCommission.durationsInDays,
  currency: olCommission.currency,
  periodIds: olCommission.periods.map((p) => p.id),
  productIds: olCommission.products.map((p) => p.id),
  advancePayment: olCommission.advancePayment,
  fa1ePrice: olCommission.fa1ePrice,
  fa2ePrice: olCommission.fa2ePrice,
  retainer: olCommission.retainer,
  adminFeePerStudent: olCommission.adminFeePerStudent
})

export const mapOlHostFamilyPriceToInput = (
  olHostFamilyPrice: OLHostFamilyPricePerNight
): OLHostFamilyPricePerNightInput => ({
  appliedToHost: olHostFamilyPrice.appliedToHost,
  productIds: olHostFamilyPrice.products.map((p) => p.id),
  periodIds: olHostFamilyPrice.periods.map((p) => p.id),
  fa1ePrice: olHostFamilyPrice.fa1ePrice,
  fa2ePrice: olHostFamilyPrice.fa2ePrice,
  visitPackPerWeekPrice: olHostFamilyPrice.visitPackPerWeekPrice,
  otoPerHourPrice: olHostFamilyPrice.otoPerHourPrice,
  currency: olHostFamilyPrice.currency
})

export const mapOlTeflPriceToInput = (olTerfPrice: OlTeflPrice): OlTeflPriceInput => ({
  productIds: olTerfPrice.products.map((p) => p.id),
  periodIds: olTerfPrice.periods.map((p) => p.id),
  activityOnlyPrice: olTerfPrice.activityOnlyPrice,
  fullDayPrice: olTerfPrice.fullDayPrice,
  tuitionAndActivityPrice: olTerfPrice.tuitionAndActivityPrice,
  tuitionOnlyPrice: olTerfPrice.tuitionOnlyPrice
})

export const ACTIVITY_FORM_SCHEMA: (
  t: TFunction<AvailableLanguages, undefined>
) => ZodType<ActivityInput> = (t) =>
  z.object({
    name: createStringSchema('Activity Name'),
    locationName: createStringSchema('Location Name'),
    phoneNumber: PHONE_NUMBER_SCHEMA(t).nullish(),
    address1: createStringSchema('Address'),
    address2: createStringSchema('Address complement', { nullish: true }),
    city: createStringSchema('Town'),
    zipCode: createStringSchema('Zip Code'),
    country: createStringSchema('Country'),
    activityLeaderFreeRatio: createEnumSchema({
      fieldName: 'Leader Free Ratio',
      enumType: LeaderFreeRateEnum
    }),
    activityFlatGroupCost: createNumberSchema('Activity Flat Group Cost', { nullish: true }),
    activitySplitAge: createNumberSchema('Activity Split Age', { nullish: true }),
    activityChildCost: createNumberSchema('Activity Child Cost', { nullish: true }),
    activityAdultCost: createNumberSchema('Activity Adult Cost', { nullish: true }),
    activityLeaderCost: createNumberSchema('Activity Leader Cost', { nullish: true }),
    transportMode: createEnumSchema({
      fieldName: 'Transport Mode',
      enumType: TransportModeEnum
    }),
    transportSplitAge: createNumberSchema('Split Age (Transport)', { nullish: true }),
    transportChildCost: createNumberSchema('Child Cost', { nullish: true }),
    transportAdultCost: createNumberSchema('Adult Cost', { nullish: true }),
    transportLeaderCost: createNumberSchema('Leader Cost', { nullish: true }),
    transportFlatGroupCost: createNumberSchema('Flat Group Cost', { nullish: true }),
    comments: createStringSchema('Comments', { nullish: true })
  })

export const COACH_COMPANY_SCHEMA_VALIDATION: (
  t: TFunction<AvailableLanguages, undefined>
) => ZodType<CoachCompanyInput> = (t) =>
  z.object({
    name: createStringSchema(t('coach.company.form.name')),
    phoneNumber: PHONE_NUMBER_SCHEMA(t),
    emergencyNumber: PHONE_NUMBER_SCHEMA(t).nullish(),
    address1: createStringSchema(t('coach.company.form.address1')),
    address2: createStringSchema(t('coach.company.form.address2'), { nullish: true }),
    city: createStringSchema(t('coach.company.form.city')),
    zipCode: createStringSchema(t('coach.company.form.zip.code')),
    country: createStringSchema(t('coach.company.form.country')),
    comments: createStringSchema(t('coach.company.form.comments'), { nullish: true })
  })

export const ARRIVAL_AND_DEPARTURE_SCHEMA_VALIDATION: (
  language: AvailableLanguages
) => ZodType<ArrivalAndDepartureInput> = (language) =>
  z.object({
    fromTo: createStringSchema('From/To', { language }),
    numberOfSeats: createNumberSchema('Number of seats', { isInteger: true, language }),
    cost: createNumberSchema('Cost', { isInteger: true, language }),
    validUntil: createStringSchema('Valid until', { nullish: true, language }),
    comment: createStringSchema('Comment', { nullish: true, language })
  })

export const QUOTATION_SCHEMA_VALIDATION: (
  language: AvailableLanguages
) => ZodType<QuotationInput> = (language) =>
  z.object({
    validUntil: createStringSchema('Valid until', { language }),
    name: createStringSchema('Name', { language }),
    file: FILE_VALIDATION({ language })
  })

export const CULTURAL_OUTING_SCHEMA_VALIDATION: (
  language: AvailableLanguages
) => ZodType<CulturalOutingInput> = (language) =>
  z.object({
    numberOfSeats: createNumberSchema('Number of seats', { isInteger: true, language }),
    validUntil: createStringSchema('Valid until', { nullish: true, language }),
    comment: createStringSchema('Comment', { nullish: true, language }),
    duration: createEnumSchema({ fieldName: 'Duration', enumType: OutingDurationEnum, language }),
    cost: createNumberSchema('Cost', { language }),
    activityId: createNumberSchema('Activity', { isInteger: true, language })
  })
